import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Phone, MessageSquare, Search, Users, Truck } from "lucide-react";
import {
  getHelpdeskContacts,
  getHelpdeskContact,
  updateHelpdeskContact,
  refreshContactLookup,
  setConversationCommerce,
} from "../../api/helpdeskContacts";
import { renameConversation, isEndpointMissing } from "../../services/aiChatService";
import PendingBackendNotice from "../ui/PendingBackendNotice";
import CommerceSelector from "./CommerceSelector";
import DriverSelector from "./DriverSelector";
import DriverPhoneEditor from "./DriverPhoneEditor";
import useAuth from "../../hooks/useAuth";
import { canWriteToDrivers } from "../../utils/roles";
import ContactAvatar from "./ContactAvatar";
import { CHANNEL_NAME } from "./ConversationList";
import MediaLightbox from "./MediaLightbox";
import { showToast } from "../../utils/showToast";
import { cn } from "../../utils/cn";
import { apiErrorMessage, apiBodyMessage } from "../../utils/apiErrorMessage";

/* Constantes de presentación A NIVEL DE MÓDULO: identidad estable entre
   renders (nada de objetos nuevos por render hacia hijos memoizados) y un solo
   vocabulario visual para todo el panel. */

// Tonos de estado para las conversaciones recientes (tokens de status, nunca hex).
const STATUS_TONES = {
  open: "bg-status-info-soft text-status-info",
  pending: "bg-status-warning-soft text-status-warning",
  assigned: "bg-status-info-soft text-status-info",
  resolved: "bg-status-success-soft text-status-success",
};
const STATUS_LABELS = {
  open: "Abierta",
  pending: "Pendiente",
  assigned: "Asignada",
  resolved: "Resuelta",
};

// Título de sección: única jerarquía de encabezado del panel (12px uppercase).
// Micro-label del sistema (11px / 600 / uppercase / tracking-wider): la misma
// métrica que los encabezados de grupo de la lista y el rótulo "Comercio".
const SECTION_TITLE = "text-[11px] uppercase tracking-wider font-semibold text-fg-muted";

// Botón secundario: ÚNICO estilo de botón del panel (44px táctil / 32px desktop).
const BTN_SECONDARY =
  "inline-flex items-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-xs font-medium border border-border bg-transparent text-fg hover:bg-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors";

// Input de texto estándar. Variantes ! porque _modern-forms.scss aplica
// padding/font-size/placeholder por selector de elemento (input[type="text"],
// especificidad 0,1,1) y le gana a las utilidades Tailwind sin important.
const INPUT_CLS =
  // !text-base bajo md: con <16px Safari iOS hace zoom al enfocar y no vuelve
  // solo. En ≥768px se conservan los 14px.
  "w-full !h-11 md:!h-8 !px-2.5 !py-0 !text-base md:!text-sm !rounded-md !border !border-border !bg-bg text-fg placeholder:!text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";

// Chip estándar (11px, alto 5) — el tono se agrega al usarlo.
// 18px / 11px / pill: misma métrica que los chips de la lista y del header del chat.
const CHIP = "inline-flex items-center h-[18px] px-2 rounded-full text-[11px] font-medium shrink-0";

// helpdeskContacts.js returns this literal when there was no response at all;
// it says nothing useful, so the specific fallback wins.
const UNKNOWN_ERROR = "Error desconocido";
const contactErrorText = (error, fallback) =>
  (error !== UNKNOWN_ERROR && apiBodyMessage(error)) || fallback;

/**
 * Panel de contacto. Dos modos de presentación:
 *   - mode="fixed":   tercera columna fija (desktop ≥ 1280px). Sin backdrop;
 *                     el botón X del header llama a onClose (colapsa la columna).
 *   - mode="overlay": modal slide-over (mobile/tablet). Renderiza backdrop y
 *                     cierra con Escape o click fuera.
 */
export default function ContactPanel({
  conversation,
  onConversationUpdate,
  onClose,
  mode = "overlay",
}) {
  const { t } = useTranslation();
  const isFixed = mode === "fixed";
  const { user } = useAuth() || {};
  const canEditDriverPhone = canWriteToDrivers(user);

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Foto de perfil ampliada (estilo WhatsApp Web).
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [scope, setScope] = useState("conversation");
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [aliasDraft, setAliasDraft] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);
  const [savingDriver, setSavingDriver] = useState(false);
  const [editingDriverPhone, setEditingDriverPhone] = useState(false);
  // Nombre editable del grupo (solo conversaciones is_group). Los grupos de
  // WhatsApp llegan con el JID numérico como identidad y aquí es donde el agente
  // espera poder ponerles un nombre.
  const [groupNameDraft, setGroupNameDraft] = useState("");
  const [savingGroupName, setSavingGroupName] = useState(false);
  // Aviso "en construcción" para el rename: solo cuando el 404/405 llega SIN
  // mensaje del backend (ruta no desplegada en ese entorno).
  const [groupNamePending, setGroupNamePending] = useState(false);

  // Id del último contacto cuyos drafts (alias/notas) se sincronizaron. Un
  // reload del MISMO contacto no debe pisar lo que el usuario está escribiendo:
  // selectedConv cambia de identidad con cada evento SSE del inbox, y cada
  // recarga hacía setAliasDraft(c.alias) borrando el texto a medio tipear.
  const syncedContactRef = useRef(null);

  // Deps por PRIMITIVOS (id/external_id/channel), no por el objeto conversation:
  // la identidad del objeto churnea en cada reconciliación SSE aunque siga
  // siendo la misma conversación, y cada churn refetcheaba el contacto.
  const convId = conversation?.id;
  const convExternalId = conversation?.external_id;
  const convChannel = conversation?.channel;
  const convIsGroup = !!conversation?.is_group;
  const convContactName = conversation?.contact_name;

  // Nombre actual del grupo: si contact_name sigue siendo el JID crudo, no hay
  // nombre puesto todavía (el JID se muestra como placeholder, no como valor).
  const currentGroupName =
    convContactName && convContactName !== convExternalId ? convContactName : "";

  // Deps por primitivos: el objeto conversation cambia de identidad en cada
  // reconciliación SSE y resincronizar por objeto borraría lo que se está
  // escribiendo (mismo motivo que syncedContactRef para el alias).
  useEffect(() => {
    setGroupNameDraft(currentGroupName);
    setGroupNamePending(false);
  }, [convId, currentGroupName]);

  const loadContact = useCallback(async () => {
    if (!convId) return;
    if (convIsGroup) {
      syncedContactRef.current = null;
      setContact(null);
      setAliasDraft("");
      setNotesDraft("");
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Same axios client as the rest of the contact API: base URL and auth come
      // from its config. A hand-built URL here dropped the "/" before
      // "helpdesk" in prod and every contact looked unsaved.
      const data = await getHelpdeskContacts({ q: convExternalId, limit: 1 });
      if (data?.error) {
        setError(t("No se pudo cargar el contacto"));
        return;
      }
      const found = (data?.contacts || []).find(
        (c) => c.external_id === convExternalId && c.channel === convChannel,
      );
      if (found) {
        const detail = await getHelpdeskContact(found.id);
        const c = detail?.contact || found;
        setContact(c);
        if (syncedContactRef.current !== c.id) {
          syncedContactRef.current = c.id;
          setNotesDraft(c.notes || "");
          setAliasDraft(c.alias || "");
        }
      } else {
        setContact(null);
        if (syncedContactRef.current !== null) {
          syncedContactRef.current = null;
          setAliasDraft("");
          setNotesDraft("");
        }
      }
    } catch (err) {
      setError(t("No se pudo cargar el contacto"));
    } finally {
      setLoading(false);
    }
  }, [convId, convExternalId, convChannel, convIsGroup, t]);

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  // Solo capturar Escape en modo overlay (en fixed la columna se colapsa con
  // el botón X o el atajo global del layout, no con Escape).
  useEffect(() => {
    if (isFixed || !onClose) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isFixed, onClose]);

  // A6 · En modo overlay esto es un diálogo modal: bloquear el scroll del body
  // como ui/Modal y ui/Drawer. Además de la ergonomía es lo que dispara la red
  // de seguridad del shell (`body[style*="overflow: hidden"] .bottom-nav`), para
  // que la barra inferior deje de capturar toques mientras el panel está
  // abierto. El cleanup restaura el valor previo también al navegar con el panel
  // abierto, así el body nunca queda pegado.
  const overlayOpen = !isFixed && !!conversation;
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [overlayOpen]);

  const currentCommerce = (() => {
    if (scope === "conversation") {
      return conversation?.effective_commerce_id
        ? {
            id: conversation.effective_commerce_id,
            name: conversation.effective_commerce_name || "",
          }
        : null;
    }
    return contact?.default_commerce_id
      ? {
          id: contact.default_commerce_id,
          name: contact.default_commerce_name || "",
        }
      : null;
  })();

  const handleCommerceChange = async (commerce) => {
    const commerceId = commerce?.id || null;
    if (scope === "conversation") {
      const result = await setConversationCommerce(conversation.id, commerceId);
      if (result?.error) {
        showToast(contactErrorText(result.error, t("No se pudo actualizar el comercio")), false);
        return;
      }
      onConversationUpdate?.(result.conversation);
      showToast(
        commerceId ? t("Comercio asignado a esta conversación") : t("Override eliminado"),
        true,
      );
    } else if (contact) {
      const result = await updateHelpdeskContact(contact.id, {
        default_commerce_id: commerceId,
      });
      if (result?.error) {
        showToast(contactErrorText(result.error, t("No se pudo actualizar el contacto")), false);
        return;
      }
      setContact(result.contact);
      showToast(t("Comercio por defecto actualizado"), true);
    }
  };

  // Repartidor asignado al contacto. Da a este número el acceso de la IA a los
  // datos del repartidor, así que quitarlo o cambiarlo es una acción explícita
  // del staff: el backend valida que sea un repartidor ACTIVO del courier.
  const saveDriver = async (driverId) => {
    if (!contact || savingDriver) return;
    setSavingDriver(true);
    const result = await updateHelpdeskContact(contact.id, { driver_id: driverId });
    setSavingDriver(false);
    if (result?.error) {
      showToast(contactErrorText(result.error, t("No se pudo actualizar el repartidor")), false);
      return;
    }
    setContact(result.contact);
    showToast(driverId ? t("Repartidor asignado") : t("Repartidor quitado"), true);
  };

  const handleDriverSelect = (driver) => {
    if (!driver?.id || driver.id === contact?.driver_id) return;
    saveDriver(driver.id);
  };

  const handleDriverPhoneSaved = () => {
    setEditingDriverPhone(false);
    showToast(t("Teléfono del repartidor actualizado"), true);
  };

  const handleDriverClear = () => {
    if (!window.confirm(t("¿Quitar el repartidor asignado a este número?"))) return;
    saveDriver(null);
  };

  const handleRefreshLookup = async () => {
    if (!contact) return;
    const result = await refreshContactLookup(contact.id);
    if (result?.error) {
      showToast(contactErrorText(result.error, t("No se pudo refrescar la búsqueda")), false);
      return;
    }
    setContact(result.contact);
    if (result.contact?.matched) {
      showToast(t("Comercio encontrado"), true);
    } else {
      showToast(t("No se encontró un comercio para este teléfono"), false);
    }
  };

  const handleNotesSave = async () => {
    if (!contact) return;
    setSavingNotes(true);
    const result = await updateHelpdeskContact(contact.id, { notes: notesDraft });
    setSavingNotes(false);
    if (result?.error) {
      showToast(contactErrorText(result.error, t("No se pudo guardar la nota")), false);
      return;
    }
    setContact(result.contact);
    showToast(t("Notas guardadas"), true);
  };

  const handleAliasSave = async () => {
    if (!contact) return;
    setSavingAlias(true);
    const result = await updateHelpdeskContact(contact.id, {
      alias: aliasDraft.trim(),
    });
    setSavingAlias(false);
    if (result?.error) {
      showToast(contactErrorText(result.error, t("No se pudo guardar el alias")), false);
      return;
    }
    setContact(result.contact);
    showToast(
      aliasDraft.trim() ? t("Alias guardado") : t("Alias eliminado"),
      true,
    );
  };

  // Renombrar el grupo. Mismo servicio que el menú overflow del hilo. El aviso
  // "pendiente de backend" es SOLO para un 404/405 sin mensaje del backend (la
  // ruta no está desplegada en ese entorno): un 404 con mensaje de Nest
  // ('Conversacion no encontrada') es un error real y va como toast.
  const handleGroupNameSave = async () => {
    const name = groupNameDraft.trim();
    if (!name || name.length > 120 || savingGroupName) return;
    setSavingGroupName(true);
    setGroupNamePending(false);
    try {
      const updated = await renameConversation(
        conversation.id,
        name,
        conversation.channel,
      );
      const conv = updated?.conversation ?? updated;
      if (conv?.id) onConversationUpdate?.(conv);
      showToast(t("Nombre del grupo actualizado"), true);
    } catch (err) {
      if (isEndpointMissing(err) && !err?.serverMessage) {
        setGroupNamePending(true);
      } else {
        showToast(apiErrorMessage(err, t("No se pudo renombrar el grupo.")), false);
      }
    } finally {
      setSavingGroupName(false);
    }
  };

  if (!conversation) return null;

  // Función simple, NO un componente inline (const Wrapper = ({children}) => …):
  // un componente definido dentro del render cambia de identidad en cada render
  // y React desmonta/remonta todo el subárbol — el input de alias perdía el
  // foco y el caret con cada tecla escrita.
  const wrap = (children) => {
    if (isFixed) {
      return (
        <aside
          aria-label={t("Detalle del contacto")}
          className="w-full h-full flex flex-col bg-bg"
        >
          {children}
        </aside>
      );
    }
    // A6 · Banda de overlays (src/scss/_bottom-nav.scss:8-26): backdrop 1200 /
    // panel 1201. Con `z-40`/`z-50` de Tailwind el panel quedaba DEBAJO del
    // header de la app (z-index 150) — su botón X caía sobre el avatar del
    // usuario y tocarlo abría el menú de sesión en vez de cerrar — y debajo de
    // la bottom-nav (1000). El `max-w` en móvil garantiza que siempre quede
    // franja de backdrop tocable: en 360px el panel ocupaba el 100% del ancho.
    return (
      <>
        <div
          className="fixed inset-0 bg-black/40"
          style={{ zIndex: 1200 }}
          onClick={onClose}
          aria-hidden="true"
        />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={t("Detalle del contacto")}
          style={{ zIndex: 1201 }}
          className="fixed top-0 right-0 h-full w-full max-w-[calc(100vw-3rem)] md:max-w-[360px] bg-bg border-l border-border flex flex-col animate-in motion-reduce:animate-none"
        >
          {children}
        </aside>
      </>
    );
  };

  const headerName =
    (contact?.alias && contact.alias.trim()) ||
    contact?.display_name ||
    conversation.contact_name ||
    conversation.external_id;

  return wrap(
    <>
      {/* ── Header propio del panel: título + cerrar (en AMBOS modos) ─────── */}
      <div className="shrink-0 h-12 px-4 border-b border-border/60 flex items-center justify-between gap-2">
        <h3 className={SECTION_TITLE}>{t("Contacto")}</h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Cerrar panel de contacto")}
            title={isFixed ? t("Cerrar panel") : t("Cerrar (Esc)")}
            className="inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors -mr-2"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* ── Estado de carga: skeleton con la forma real del panel ──────────── */}
      {loading && (
        <div className="flex-1 overflow-y-auto px-4 py-4 animate-pulse" aria-hidden="true">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-bg-muted shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="h-3 w-2/3 rounded bg-bg-muted" />
              <div className="h-3 w-1/3 rounded bg-bg-muted" />
            </div>
          </div>
          <div className="mt-8 space-y-2">
            <div className="h-3 w-1/4 rounded bg-bg-muted" />
            <div className="h-8 rounded-md bg-bg-muted" />
          </div>
          <div className="mt-8 space-y-2">
            <div className="h-3 w-1/4 rounded bg-bg-muted" />
            <div className="h-8 rounded-md bg-bg-muted" />
          </div>
        </div>
      )}

      {/* ── Contenido: secciones planas separadas por divisores ────────────── */}
      {!loading && (
        <div className="flex-1 overflow-y-auto divide-y divide-border/60">
          {error && (
            <section className="px-4 py-4">
              <div className="px-3 py-2 rounded-md bg-status-error-soft text-status-error text-xs">
                {error}
              </div>
              <button type="button" onClick={loadContact} className={cn(BTN_SECONDARY, "mt-2")}>
                {t("Reintentar")}
              </button>
            </section>
          )}

          {/* Grupos: no hay contacto único, pero sí un nombre editable */}
          {conversation.is_group && (
            <>
              <section className="px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-bg-muted text-fg-muted flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4" aria-hidden="true" />
                  </div>
                  <div
                    className="flex-1 min-w-0 text-sm font-semibold text-fg truncate"
                    title={currentGroupName || conversation.external_id}
                  >
                    {currentGroupName || conversation.external_id}
                  </div>
                  <span className={cn(CHIP, "bg-status-info-soft text-status-info")}>
                    {t("Grupo")}
                  </span>
                </div>
                <p className="text-xs text-fg-muted leading-relaxed mt-3">
                  {t("Esta conversación es un grupo de WhatsApp. No hay un contacto único asociado: cada mensaje puede venir de un participante distinto.")}
                </p>
              </section>

              <section className="px-4 py-4">
                <label
                  htmlFor={`hd-group-name-${conversation.id}`}
                  className={cn("hd-field-label block mb-2", SECTION_TITLE)}
                >
                  {t("Nombre del grupo")}
                </label>
                <p className="text-xs text-fg-muted leading-relaxed mb-2">
                  {t("Este nombre solo se usa en el helpdesk; no cambia el nombre del grupo en WhatsApp.")}
                </p>
                <input
                  id={`hd-group-name-${conversation.id}`}
                  type="text"
                  value={groupNameDraft}
                  onChange={(e) => setGroupNameDraft(e.target.value)}
                  placeholder={conversation.external_id || t("Nombre del grupo")}
                  maxLength={120}
                  disabled={savingGroupName}
                  className={INPUT_CLS}
                />
                <button
                  type="button"
                  onClick={handleGroupNameSave}
                  disabled={
                    savingGroupName ||
                    !groupNameDraft.trim() ||
                    groupNameDraft.trim() === currentGroupName
                  }
                  className={cn(BTN_SECONDARY, "mt-2")}
                >
                  {savingGroupName ? t("Guardando…") : t("Guardar nombre")}
                </button>
                {groupNamePending && (
                  <PendingBackendNotice className="mt-2">
                    {t("Renombrar grupos está en construcción.")}
                  </PendingBackendNotice>
                )}
              </section>
            </>
          )}

          {!conversation.is_group && (
            <>
              {/* Identidad */}
              <section className="px-4 py-4">
                <div className="flex items-center gap-3">
                  <ContactAvatar
                    src={conversation.contact_profile_pic}
                    initial={(headerName?.trim()?.[0] || "?").toUpperCase()}
                    toneClass="bg-bg-muted text-fg"
                    className="w-10 h-10 text-sm shrink-0"
                    onImageClick={() => setAvatarOpen(true)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-fg truncate" title={headerName}>
                      {headerName}
                    </div>
                    {contact?.alias &&
                      contact?.display_name &&
                      contact.alias.trim() !== contact.display_name && (
                        <div className="text-xs text-fg-muted truncate">
                          WhatsApp: {contact.display_name}
                        </div>
                      )}
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-fg-muted">
                    <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span className="truncate">
                      {contact?.phone || conversation.contact_phone || conversation.external_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-fg-muted">
                    <MessageSquare className="w-4 h-4 shrink-0" aria-hidden="true" />
                    {/* Nombre legible del canal (misma fuente que la lista y el
                        Excel); el valor crudo del enum solo si no está mapeado. */}
                    <span>
                      {CHANNEL_NAME[conversation.channel]
                        ? t(CHANNEL_NAME[conversation.channel])
                        : conversation.channel}
                    </span>
                  </div>
                </div>
                {/* Contacto aún no persistido: identidad desde la conversación */}
                {!contact && !error && (
                  <p className="text-xs text-fg-muted leading-relaxed mt-3">
                    {t("Este contacto aún no está guardado en la mesa de ayuda.")}
                  </p>
                )}
              </section>

              {/* Alias */}
              {contact && (
                <section className="px-4 py-4">
                  <label
                    htmlFor={`hd-alias-${conversation.id}`}
                    className={cn("hd-field-label block mb-2", SECTION_TITLE)}
                  >
                    {t("Alias")}
                  </label>
                  <p className="text-xs text-fg-muted leading-relaxed mb-2">
                    {t("Sobreescribe el nombre que muestra el canal.")}
                  </p>
                  <input
                    id={`hd-alias-${conversation.id}`}
                    type="text"
                    value={aliasDraft}
                    onChange={(e) => setAliasDraft(e.target.value)}
                    placeholder={contact.display_name || t("Sin alias")}
                    maxLength={120}
                    className={INPUT_CLS}
                  />
                  <button
                    type="button"
                    onClick={handleAliasSave}
                    disabled={savingAlias || aliasDraft === (contact.alias || "")}
                    className={cn(BTN_SECONDARY, "mt-2")}
                  >
                    {savingAlias
                      ? t("Guardando…")
                      : aliasDraft.trim()
                      ? t("Guardar alias")
                      : t("Eliminar alias")}
                  </button>
                </section>
              )}

              {/* Comercio */}
              <section className="px-4 py-4">
                <h4 className={cn("mb-2", SECTION_TITLE)}>{t("Comercio")}</h4>

                {/* El ámbito "por defecto" escribe sobre el contacto: solo tiene
                    sentido cuando el contacto existe en la mesa de ayuda. */}
                {contact && (
                  <div
                    role="radiogroup"
                    aria-label={t("Ámbito del comercio")}
                    className="flex items-center gap-4 mb-2"
                  >
                    <label className="inline-flex items-center gap-2 min-h-[44px] md:min-h-0 mb-0 cursor-pointer text-xs text-fg">
                      <input
                        type="radio"
                        name={`commerce-scope-${conversation.id}`}
                        value="conversation"
                        checked={scope === "conversation"}
                        onChange={() => setScope("conversation")}
                        className="accent-brand-500 w-4 h-4"
                      />
                      {t("Esta conversación")}
                    </label>
                    <label className="inline-flex items-center gap-2 min-h-[44px] md:min-h-0 mb-0 cursor-pointer text-xs text-fg">
                      <input
                        type="radio"
                        name={`commerce-scope-${conversation.id}`}
                        value="default"
                        checked={scope === "default"}
                        onChange={() => setScope("default")}
                        className="accent-brand-500 w-4 h-4"
                      />
                      {t("Por defecto del contacto")}
                    </label>
                  </div>
                )}

                <CommerceSelector
                  value={currentCommerce}
                  onChange={handleCommerceChange}
                  placeholder={t("Asignar comercio…")}
                  size="sm"
                />

                {contact && (
                  <button
                    type="button"
                    onClick={handleRefreshLookup}
                    className={cn(BTN_SECONDARY, "mt-2")}
                  >
                    <Search className="w-4 h-4" aria-hidden="true" />
                    {t("Buscar en BD de comercios")}
                  </button>
                )}
              </section>

              {/* Repartidor asignado: vive en el contacto (no por conversación) */}
              {contact && (
                <section className="px-4 py-4">
                  <h4 className={cn("mb-2", SECTION_TITLE)}>{t("Repartidor asignado")}</h4>
                  <p className="text-xs text-fg-muted leading-relaxed mb-2">
                    {t("Asigna el repartidor solo si validaste que este número es suyo: la IA le dará acceso a sus pedidos.")}
                  </p>
                  {contact.driver_id && (
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="w-4 h-4 text-brand-600 shrink-0" aria-hidden="true" />
                      <span
                        className="flex-1 min-w-0 text-sm text-fg truncate"
                        title={contact.driver_name || ""}
                      >
                        {contact.driver_name || t("Repartidor sin nombre")}
                      </span>
                      <button
                        type="button"
                        onClick={handleDriverClear}
                        disabled={savingDriver}
                        className={BTN_SECONDARY}
                      >
                        {t("Quitar")}
                      </button>
                    </div>
                  )}
                  {contact.driver_id && canEditDriverPhone && (
                    <div className="mb-2">
                      {editingDriverPhone ? (
                        <DriverPhoneEditor
                          key={contact.driver_id}
                          driverId={contact.driver_id}
                          onSaved={handleDriverPhoneSaved}
                          onCancel={() => setEditingDriverPhone(false)}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingDriverPhone(true)}
                          disabled={savingDriver}
                          className={BTN_SECONDARY}
                        >
                          {t("Editar teléfono registrado")}
                        </button>
                      )}
                    </div>
                  )}
                  <DriverSelector
                    value={
                      contact.driver_id
                        ? { id: contact.driver_id, name: contact.driver_name || "" }
                        : null
                    }
                    onChange={handleDriverSelect}
                    disabled={savingDriver}
                    placeholder={
                      contact.driver_id ? t("Cambiar repartidor…") : t("Asignar repartidor…")
                    }
                  />
                </section>
              )}

              {/* The contact-tags placeholder ("Etiquetas · Próximamente") was
                  removed in the second visual pass: it had no behavior, and
                  next to the real conversation labels in the header it read
                  as a broken feature. */}

              {/* Conversaciones recientes: filas planas, sin mini-cards */}
              {contact?.conversations && contact.conversations.length > 0 && (
                <section className="px-4 py-4">
                  <h4 className={cn("mb-2", SECTION_TITLE)}>
                    {t("Conversaciones recientes")}
                  </h4>
                  <ul className="space-y-1">
                    {contact.conversations.map((c) => (
                      <li
                        key={c.id}
                        className={cn(
                          "flex items-center gap-2 rounded-md px-2 py-1.5 -mx-2",
                          c.id === conversation.id && "bg-bg-subtle",
                        )}
                      >
                        <span
                          className={cn(
                            CHIP,
                            STATUS_TONES[c.status] || "bg-bg-muted text-fg-muted",
                          )}
                        >
                          {STATUS_LABELS[c.status] ? t(STATUS_LABELS[c.status]) : c.status}
                        </span>
                        <span className="flex-1 min-w-0 text-xs text-fg-muted truncate">
                          {c.last_message_preview || "—"}
                        </span>
                        {c.id === conversation.id && (
                          <span className={cn(CHIP, "bg-brand-50 text-brand-700")}>
                            {t("Actual")}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Notas internas */}
              {contact && (
                <section className="px-4 py-4">
                  <label
                    htmlFor={`hd-notes-${conversation.id}`}
                    className={cn("hd-field-label block mb-2", SECTION_TITLE)}
                  >
                    {t("Notas internas")}
                  </label>
                  <textarea
                    id={`hd-notes-${conversation.id}`}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder={t("Notas privadas sobre este contacto…")}
                    maxLength={2000}
                    className="w-full !min-h-[96px] !px-2.5 !py-2 !text-base md:!text-sm !rounded-md !border !border-border !bg-bg text-fg placeholder:!text-fg-muted resize-y focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  />
                  <button
                    type="button"
                    onClick={handleNotesSave}
                    disabled={savingNotes || notesDraft === (contact.notes || "")}
                    className={cn(BTN_SECONDARY, "mt-2")}
                  >
                    {savingNotes ? t("Guardando…") : t("Guardar notas")}
                  </button>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {avatarOpen && conversation.contact_profile_pic && (
        <MediaLightbox
          url={conversation.contact_profile_pic}
          caption={headerName}
          profile
          onClose={() => setAvatarOpen(false)}
        />
      )}
    </>,
  );
}
